import { Modal, Form, InputNumber, Button, Typography, Checkbox, Statistic, Space, Flex, Card, Alert } from 'antd'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import React, { useEffect, useState } from 'react'

import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons'
import { SchoolService } from '@services/SchoolService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { Product, School, Subscription } from '../../../../types'

import { BillableActionsTable } from './BillableActionsTable'

dayjs.extend(weekday)
dayjs.extend(localeData)

interface EditSubscriptionModalProps {
    isOpen: boolean
    onClose: () => void
    products: Product[]
    seatCount: number
    subscription: Subscription | null // Replace 'any' with the actual Subscription type
    school: School | null
    refetch: () => Promise<void>
}

export const SeatChangeModal: React.FC<EditSubscriptionModalProps> = ({
    isOpen,
    onClose,
    school,
    seatCount,
    subscription,
    refetch
}) => {
    const confirm = useConfirmation()
    const [form] = Form.useForm()
    const [isLoading, setIsLoading] = useState(false)
    const { showNotification } = useNotifications()
    const [seatDiff, setSeatDiff] = useState<number>(0)
    const [showBillables, setShowBillables] = useState<boolean>(false)
    const issueRefund = Form.useWatch('issueRefund', form)
    const skipNotification = Form.useWatch('skipNotification', form)

    useEffect(() => {
        if (subscription && school) {
            form.setFieldsValue({
                seatCount,
                issueRefund: false
            })
        }
    }, [subscription, form])

    const handleSubmit = async (values: any) => {
        if (!school || !subscription) {
            return
        }
        const change = values.seatCount - seatCount

        let keepGoing = true
        if (seatCount > values.seatCount) {
            // reducing seat count - WARN
            const description = values.issueRefund
                ? `The creator will be refunded for ${Math.abs(change)} seats`
                : 'The creator will not be issued a refund for the seat change'

            keepGoing = await confirm({
                title: 'Please confirm the seat decrease',
                description
            })
        } else if (seatCount < values.seatCount) {
            // increasing seat count - CONFIRM
            keepGoing = await confirm({
                title: 'Please confirm the seat increase',
                description: `The creator will be billed for ${change} additional seats`
            })
        }

        if (keepGoing) {
            const billable = change > 0 ? true : values.issueRefund

            try {
                setIsLoading(true)
                await SchoolService.updateSubscriptionSeats(school.id, subscription.id, {
                    seatCount: values.seatCount,
                    billable,
                    skipNotification: values.skipNotification
                })
                onClose()
                await refetch()
            } catch (error) {
                showNotification({
                    title: 'Error updating subscription',
                    type: 'error'
                })
            } finally {
                setIsLoading(false)
            }
        }
    }

    if (!subscription) {
        return null
    }

    if (subscription.status === 'CANCELLED') {
        return null
    }

    const changeStat = {
        prefix: <></>,
        color: 'black'
    }
    if (seatDiff > 0) {
        changeStat.prefix = <ArrowUpOutlined />
        changeStat.color = 'green'
    } else if (seatDiff < 0) {
        changeStat.prefix = <ArrowDownOutlined />
        changeStat.color = '#cf1322'
    }

    const width = showBillables ? 700 : 400

    return (
        <Modal width={width} title="Edit Subscription" open={isOpen} onCancel={onClose} footer={null}>
            {subscription.stripeId && <Typography.Text>Stripe Subscriptions must be edited in Stripe </Typography.Text>}

            {!subscription.stripeId && (
                <Form form={form} onFinish={handleSubmit}>
                    <Space vertical style={{ width: '100%' }} size="large">
                        <Typography.Title level={5}>Name: {subscription.name}</Typography.Title>
                        <Space size="large">
                            <Form.Item
                                name="seatCount"
                                label="Seat Count"
                                initialValue={seatCount}
                                rules={[{ required: true, message: 'Please enter a seat count' }]}
                            >
                                <InputNumber
                                    min={1}
                                    onChange={newSeatCount => {
                                        if (newSeatCount) {
                                            setSeatDiff(newSeatCount - seatCount)
                                        }
                                    }}
                                    style={{ marginBottom: 0 }}
                                />
                            </Form.Item>
                            <Card>
                                <Statistic
                                    title="Seat Change"
                                    value={seatDiff}
                                    precision={0}
                                    styles={{ content: { color: changeStat.color } }}
                                    prefix={changeStat.prefix}
                                />
                            </Card>
                        </Space>
                        {seatDiff > 0 && (
                            <>
                                <Space vertical>
                                    <Form.Item
                                        name="skipNotification"
                                        label="Skip Notification"
                                        valuePropName="checked"
                                        initialValue={false}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Checkbox />
                                    </Form.Item>
                                    {!skipNotification && (
                                        <Alert
                                            title={`The administrator of this school will be notified of the seat update`}
                                        />
                                    )}
                                    {skipNotification && (
                                        <Alert
                                            title={`The administrator of this school will NOT be notified of the seat update`}
                                            type="warning"
                                        />
                                    )}
                                </Space>
                            </>
                        )}
                        {!subscription.isInternal && (
                            <>
                                {seatDiff > 0 && (
                                    <Alert
                                        title={`This creator will be billed for ${seatDiff} additional seats at the next billing cycle`}
                                        // type="warning"
                                    />
                                )}
                                {seatDiff < 0 && (
                                    <Space vertical>
                                        <Form.Item
                                            name="issueRefund"
                                            label="Issue Refund"
                                            valuePropName="checked"
                                            initialValue={false}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Checkbox />
                                        </Form.Item>
                                        <Typography.Text style={{ fontSize: '10px' }}>
                                            Refund Policy: Refunds are to the discretion of the manager in charge
                                        </Typography.Text>
                                    </Space>
                                )}
                                {seatDiff < 0 && issueRefund && (
                                    <Alert
                                        title={`This creator will be refunded ${Math.abs(
                                            seatDiff
                                        )} seat(s) at the next billing cycle`}
                                        type="warning"
                                    />
                                )}
                            </>
                        )}

                        <Flex justify="space-between">
                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={isLoading}
                                    disabled={isLoading || seatDiff === 0}
                                >
                                    Update Seat Count
                                </Button>
                            </Form.Item>
                            {!subscription.isInternal && (
                                <Button type="link" onClick={() => setShowBillables(!showBillables)}>
                                    {showBillables ? 'Hide Billables' : 'View billables'}
                                </Button>
                            )}
                        </Flex>
                    </Space>
                </Form>
            )}
            {showBillables && school && (
                <>
                    <Typography.Title level={3}>All billable actions on this subscription</Typography.Title>
                    <BillableActionsTable schoolId={school.id} subscriptionId={subscription.id}></BillableActionsTable>
                </>
            )}
        </Modal>
    )
}
